import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { RotateCcw, Search, Trash2 } from 'lucide-react';
import FormPageShell from '../../components/Common/FormPageShell';
import PageHeader from '../../components/Common/PageHeader';
import RecordHistoryDrawer from '../../components/Audit/RecordHistoryDrawer';
import { formatActor, formatAuditDate } from '../../components/Audit/auditLabels';
import { getAppHomePath } from '../../constants/navigation';
import { auditService } from '../../services/api';
import { userFacingError } from '../../i18n/userFacingError';

const Trash = () => {
  const queryClient = useQueryClient();
  const [entityType, setEntityType] = useState('');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);

  const { data: catalogData } = useQuery(['audit-catalog'], async () => (await auditService.catalog()).data);
  const catalog = catalogData?.items || [];
  const filters = useMemo(
    () => ({
      entity_type: entityType || undefined,
      q: q.trim() || undefined,
      limit: 200,
    }),
    [entityType, q]
  );

  const { data, isLoading, error } = useQuery(
    ['audit-trash', filters],
    async () => (await auditService.trash(filters)).data
  );
  const items = data?.items || [];

  const restoreMutation = useMutation(
    ({ type, id }) => auditService.restore(type, id),
    {
      onSuccess: () => {
        toast.success('Registro restaurado. Ele volta a aparecer nos cadastros.');
        queryClient.invalidateQueries(['audit-trash']);
        queryClient.invalidateQueries(['audit-history']);
      },
      onError: (err) => toast.error(userFacingError(err, 'Não foi possível restaurar.')),
    }
  );

  return (
    <FormPageShell maxWidth="max-w-7xl">
      <PageHeader
        title="Lixeira"
        description="Itens excluídos logicamente. Somente o administrador pode restaurar."
        breadcrumbs={[{ label: 'Início', to: getAppHomePath() }, { label: 'Lixeira' }]}
      />

      <div className="card mt-4">
        <div className="card-content space-y-4">
          <div className="grid gap-3 md:grid-cols-2">
            <label className="block text-sm">
              <span className="mb-1 block text-gray-600">Cadastro</span>
              <select className="input" value={entityType} onChange={(e) => setEntityType(e.target.value)}>
                <option value="">Todos</option>
                {catalog.map((item) => (
                  <option key={item.entity_type} value={item.entity_type}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-gray-600">Buscar</span>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <input
                  className="input pl-9"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Nome ou identificador…"
                />
              </div>
            </label>
          </div>

          {isLoading ? (
            <p className="text-sm text-gray-500">Carregando lixeira…</p>
          ) : error ? (
            <p className="text-sm text-rose-600">Não foi possível carregar a lixeira.</p>
          ) : items.length === 0 ? (
            <div className="flex items-center gap-3 rounded-lg border border-dashed border-gray-200 p-6 text-sm text-gray-500">
              <Trash2 className="h-5 w-5" />
              Nenhum registro excluído no momento.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Excluído em</th>
                    <th>Cadastro</th>
                    <th>Registro</th>
                    <th>Excluído por</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={`${item.entity_type}-${item.entity_id}`}>
                      <td className="whitespace-nowrap text-sm text-gray-600">{formatAuditDate(item.deleted_at)}</td>
                      <td className="text-sm text-gray-800">{item.entity_type_label || item.entity_type}</td>
                      <td className="text-sm text-gray-800">{item.entity_label || item.entity_id}</td>
                      <td className="text-sm text-gray-700">{formatActor(item)}</td>
                      <td>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            className="btn btn-outline btn-sm"
                            onClick={() => setSelected(item)}
                          >
                            Histórico
                          </button>
                          <button
                            type="button"
                            className="btn btn-primary btn-sm inline-flex items-center gap-1"
                            disabled={restoreMutation.isLoading}
                            onClick={() => {
                              if (!window.confirm(`Restaurar «${item.entity_label || item.entity_id}»?`)) return;
                              restoreMutation.mutate({ type: item.entity_type, id: item.entity_id });
                            }}
                          >
                            <RotateCcw className="h-3.5 w-3.5" />
                            Restaurar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {selected ? (
        <RecordHistoryDrawer
          entityType={selected.entity_type}
          entityId={selected.entity_id}
          title={selected.entity_label || 'Histórico do registro'}
          onClose={() => setSelected(null)}
        />
      ) : null}
    </FormPageShell>
  );
};

export default Trash;
